<template>
  <div>
    <v-navigation-drawer
      v-model="isOpen"
      :width="$isMobile ? '100%' : '50%'"
      right
      fixed
      :permanent="isOpen"
      hide-overlay
      class="z-index--100">
      <div class="ma-3">
        <div class="d-flex">
          <v-btn
            dark
            fab
            x-small
            outlined
            color="primary"
            class="mr-3"
            @click="closePanel"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <div class="subtitle-1 primary--text">
            Platillos de {{ selectedCombo.$.name }}
          </div>
          <v-spacer/>
          <div class="subtitle-1" v-if="selectedCombo.$.cost">
            Total: ${{ selectedCombo.$.cost | toFix2 | numberWithComma }}
          </div>
        </div>
        <div>
          <div>
            <v-row>
              <v-col md="12">
                <v-form ref="comboForm" v-model="form.valid">
                  <v-row>
                    <v-col cols="6">
                      <v-autocomplete
                        v-model="form.data.recipe"
                        rounded
                        outlined
                        dense
                        hide-selected
                        clearable
                        :disabled="form.disabled"
                        :items="recipesList.models"
                        item-value="uuid"
                        item-text="name"
                        :rules="[rules.required, rules.string]"
                        :label="$t('combos.labels.recipe')"
                        :placeholder="$t('combos.placeholders.recipe')"
                        :search-input.sync="searchRecipe"/>
                    </v-col>
                    <v-col cols="6">
                      <v-text-field
                        v-model.number="form.data.quantity"
                        rounded
                        outlined
                        dense
                        :rules="[rules.required, rules.numeric]"
                        :label="$t('combos.labels.quantity')"
                        :placeholder="$t('combos.placeholders.quantity')"/>
                    </v-col>
                    <v-col class="d-flex justify-end">
                      <v-btn
                        small
                        rounded
                        depressed
                        color="primary"
                        @click="submit">
                        Agregar platillo
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-col>
              <v-col md="12">
                <v-data-table
                  :loading-text="table.title"
                  :server-items-length="table.total"
                  :loading="table.loading"
                  :headers="table.headers"
                  :items="table.data"
                  :items-per-page="table.itemsPerPage"
                  :page="table.page"
                  @update:options="updateTable"
                  dense
                  class="elevation-1">
                  <template v-slot:item.quantity="{ item }">
                    <div class="text-right">
                      {{ item.quantity | toFixNumber(2) | numberWithComma }}
                    </div>
                  </template>
                  <template v-slot:item.price="{ item }">
                    <div class="text-right">
                      ${{ item.price | toFix2 | numberWithComma }}
                    </div>
                  </template>
                  <template v-slot:item.recipe.cost="{ item }">
                    <div class="text-right">
                      ${{ item.recipe.cost | toFix2 | numberWithComma }}
                    </div>
                  </template>
                  <template v-slot:item.recipe="{ item }">
                    <div class="text-right">
                      ${{ calculateCost(item) | toFix2 | numberWithComma }}
                    </div>
                  </template>
                  <template v-slot:item.action="{ item }">
                    <v-btn
                      x-small
                      fab
                      depressed
                      text
                      class="mr-2"
                      color="primary"
                      @click="editRecipeCombo(item)">
                      <v-icon small>mdi-border-color</v-icon>
                    </v-btn>
                    <v-btn
                      x-small
                      fab
                      depressed
                      text
                      class="mr-2"
                      color="error"
                      @click="openRemoveDialog(item)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </div>
        </div>
      </div>
    </v-navigation-drawer>

    <v-dialog @keydown.esc="removeDialog=false" v-model="removeDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('ingredients.actions.remove', { name: recipeToRemove.name }) }}</span>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="black darken-1" text @click="closeRemoveDialog">{{ $t('messages.no') }}</v-btn>
          <v-btn color="black darken-1" text @click="remove">{{ $t('messages.yes') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import Combo from '@/models/Combo'
import Recipe from '@/models/Recipe'
import RecipesList from '@/models/collections/RecipesList'
import { required, numeric, string } from 'vue-mc/validation'
import { debounce } from 'lodash'

export default {
  name: 'ComboRecipes',
  props: ['combo', 'isOpen'],
  data () {
    let selectedCombo = new Combo()
    let recipesList = new RecipesList()
    let recipe = new Recipe({}, recipesList)

    return {
      overlay: false,
      removeDialog: false,
      recipeToRemove: {},
      recipe,
      recipesList,
      table: {
        title: this.$i18n.t('combos.loading'),
        loading: true,
        headers: [
          { text: this.$i18n.t('combos.headers.name'), value: 'recipe.name' },
          { text: this.$i18n.t('combos.headers.quantity'), value: 'quantity' },
          { text: this.$i18n.t('combos.headers.unitCost'), value: 'recipe.cost' },
          { text: this.$i18n.t('combos.headers.calculatedCost'), value: 'recipe' },
          { text: this.$i18n.t('combos.headers.actions'), value: 'action', sortable: false }
        ],
        data: [],
        total: 0,
        itemsPerPage: 15,
        page: 1
      },
      form: {
        valid: false,
        data: {
          recipe: null,
          quantity: null
        },
        disabled: false,
        isEditing: false
      },
      rules: {
        required,
        numeric,
        string
      },
      searchRecipe: '',
      selectedCombo
    }
  },
  watch: {
    combo: async function (val) {
      if (val.$ && val.$.id) {
        this.selectedCombo = val
        await this.load()
      }
    },
    searchRecipe: debounce(async function (val) {
      if (val && val.length > 3) {
        const params = {
          restaurant: this.$store.state.currentRestaurant.data._id,
          name: `/${val}/i`,
          sort: 'name',
          status: 'attached',
          subrecipe: false
        }
        const { response } = await this.recipe.fetch({ params })
        this.recipesList.replace([response.data.data])
      }
    }, 300)
  },
  mounted () {
    window.addEventListener('keydown', this.keyDownHandler)
  },
  destroyed () {
    window.removeEventListener('keydown', this.keyDownHandler)
  },
  methods: {
    keyDownHandler (event) {
      if (event.code === 'Escape') {
        this.resetForm()
        this.$emit('close', false)
      }
    },
    updateTable: async function (data) {
      if (this.combo.$ && this.combo.$.id) {
        this.table.itemsPerPage = data.itemsPerPage
        this.table.page = data.page
        await this.load()
      }
    },
    load: async function (params = null) {
      params = params || {
        limit: this.table.itemsPerPage,
        skip: (this.table.itemsPerPage * (this.table.page - 1))
      }
      this.table.loading = true
      try {
        const { response } = await this.selectedCombo.detail()
        this.table.data = response.data.recipes
        this.table.total = response.data.recipes.length
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.table.loading = false
    },
    resetForm () {
      if (this.$refs.comboForm) this.$refs.comboForm.reset()

      this.form.data = {
        recipe: null,
        quantity: null
      }
      this.form.disabled = false
      this.form.isEditing = false
    },
    submit: async function () {
      this.$refs.comboForm.validate()

      if (this.form.valid) {
        this.overlay = true
        try {
          const combo = this.combo
          if (this.form.isEditing) {
            combo.set({ recipe: this.form.data.recipe })
          }
          const request = (this.form.isEditing)
            ? combo.updateDishes(this.form.data)
            : combo.addDishes(this.form.data)
          const { response } = await request
          this.selectedCombo = new Combo(response.data)
          this.table.data = response.data.recipes
          this.table.total = response.data.recipes.length
          this.$emit('updated', this.selectedCombo)
          this.resetForm()
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.overlay = false
      }
    },
    openRemoveDialog (item) {
      this.recipeToRemove = item
      this.removeDialog = true
    },
    closeRemoveDialog () {
      this.recipeToRemove = {}
      this.removeDialog = false
    },
    editRecipeCombo (item) {
      const recipe = item.recipe
      recipe.uuid = item.recipe.id
      this.recipesList.replace([recipe])
      this.form.data = {
        recipe: item.recipe.id,
        quantity: item.quantity
      }
      this.form.disabled = true
      this.form.isEditing = true
    },
    remove: async function () {
      this.overlay = true
      try {
        const combo = this.combo
        combo.set({ recipe: this.recipeToRemove.recipe.id })
        const { response } = await combo.removeDishes()
        this.selectedCombo = new Combo(response.data)
        this.table.data = response.data.recipes
        this.table.total = response.data.recipes.length
        this.closeRemoveDialog()
        this.$emit('updated', this.selectedCombo)
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    closePanel () {
      this.resetForm()
      this.$emit('close', false)
    },
    calculateCost (item) {
      return item.recipe.cost * item.quantity
    }
  }
}
</script>
